import React, { useState, useEffect } from 'react';
import { GitBranch, Plus, Play, Pause, Trash2, Edit2, TrendingUp, Loader2, ChevronRight, Workflow, Sparkles } from 'lucide-react';
import axios from 'axios';
import toast from '../utils/toast';
import useConfirm from '../hooks/useConfirm';
import './AutomationPage.css';

const AutomationPage = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchRules();
    fetchStats();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/automation/rules');
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/automation/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const response = await axios.post(`/api/automation/rules/${ruleId}/toggle`);
      toast.success(`Rule ${response.data.status}`);
      fetchRules();
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Failed to toggle rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!(await confirm({
      title: 'Delete rule',
      message: 'Are you sure you want to delete this rule?',
      confirmText: 'Delete'
    }))) {
      return;
    }

    try {
      await axios.delete(`/api/automation/rules/${ruleId}`);
      toast.success('Rule deleted successfully');
      fetchRules();
      fetchStats();
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  if (loading) {
    return (
      <div className="automation-loading">
        <Loader2 />
      </div>
    );
  }

  return (
    <div className="automation-page">
      {confirmDialog}
      <div className="automation-container">
        {/* Header */}
        <div className="automation-header">
          <div className="automation-header-content">
            <div className="automation-icon-box">
              <GitBranch />
            </div>
            <div className="automation-title-section">
              <h1>
                Workflows
                <Sparkles className="automation-sparkle" />
              </h1>
              <p>Automate your outreach with smart, behavior-triggered actions</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-create-workflow"
          >
            <Plus />
            Create Workflow
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="automation-stats-grid">
            <StatCard
              label="Total Workflows"
              value={stats.total_rules}
              icon={<GitBranch className="w-6 h-6" />}
            />
            <StatCard
              label="Active"
              value={stats.active_rules}
              icon={<Play className="w-6 h-6" />}
              highlight
            />
            <StatCard
              label="Total Runs"
              value={stats.total_executions}
              icon={<TrendingUp className="w-6 h-6" />}
            />
            <StatCard
              label="Success Rate"
              value={`${stats.success_rate}%`}
              icon={<Sparkles className="w-6 h-6" />}
              success
            />
          </div>
        )}

        {/* Workflows List */}
        {rules.length === 0 ? (
          <div className="automation-empty-state">
            <div className="automation-empty-icon">
              <GitBranch />
            </div>
            <h3>No workflows yet</h3>
            <p>Create your first workflow to automate responses, tag leads, and save time</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-create-workflow"
            >
              Create Your First Workflow
            </button>

            {/* Example Workflows */}
            <div className="example-rules-section">
              <h4 className="example-rules-header">
                <Sparkles />
                Example Workflows:
              </h4>
              <div className="example-rules-list">
                <ExampleRule
                  title="Hot Lead Follow-up"
                  description="When a buyer responds positively, tag as 'Hot Lead' and send follow-up email"
                />
                <ExampleRule
                  title="Not Interested - Mark DNC"
                  description="When buyer says 'not interested', automatically mark as Do Not Contact"
                />
                <ExampleRule
                  title="Engagement Scoring"
                  description="Tag buyers who respond more than 2 times as 'Engaged'"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="automation-rules-list">
            {rules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                onToggle={() => handleToggleRule(rule.id)}
                onDelete={() => handleDeleteRule(rule.id)}
              />
            ))}
          </div>
        )}

        {/* Create Rule Modal */}
        {showCreateModal && (
          <CreateRuleModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchRules();
              fetchStats();
            }}
          />
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, highlight, success }) => (
  <div className={`stat-card ${highlight ? 'highlight' : ''} ${success ? 'success' : ''}`}>
    <div className="stat-card-content">
      <div className="stat-card-icon">
        {icon}
      </div>
      <div className="stat-card-info">
        <p className="stat-card-label">{label}</p>
        <p className="stat-card-value">{value}</p>
      </div>
    </div>
  </div>
);

const RuleCard = ({ rule, onToggle, onDelete }) => {
  const isActive = rule.status === 'active';

  return (
    <div className={`rule-card ${!isActive ? 'inactive' : ''}`}>
      <div className="rule-card-header">
        <div className="rule-card-info">
          <h3 className="rule-card-name">
            {rule.name}
            <span className={`rule-status-badge ${isActive ? 'active' : 'inactive'}`}>
              {rule.status}
            </span>
          </h3>
          <p className="rule-card-desc">{rule.description}</p>
        </div>

        <div className="rule-card-actions">
          <button
            onClick={onToggle}
            className={`btn-rule-action toggle ${!isActive ? 'inactive' : ''}`}
            title={isActive ? 'Pause rule' : 'Activate rule'}
          >
            {isActive ? <Pause /> : <Play />}
          </button>

          <button
            onClick={onDelete}
            className="btn-rule-action delete"
            title="Delete rule"
          >
            <Trash2 />
          </button>
        </div>
      </div>

      <div className="rule-card-details">
        <div className="rule-detail-item">
          <div className="rule-detail-icon">
            <GitBranch />
          </div>
          <div className="rule-detail-info">
            <p className="rule-detail-label">Trigger</p>
            <p className="rule-detail-value">{rule.trigger_type.replace('_', ' ')}</p>
          </div>
        </div>

        <div className="rule-detail-item">
          <div className="rule-detail-icon">
            <TrendingUp />
          </div>
          <div className="rule-detail-info">
            <p className="rule-detail-label">Executions</p>
            <p className="rule-detail-value">{rule.execution_count || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExampleRule = ({ title, description }) => (
  <div className="example-rule-card">
    <h5 className="example-rule-title">
      <ChevronRight />
      {title}
    </h5>
    <p className="example-rule-desc">{description}</p>
  </div>
);

const CreateRuleModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'response_received',
    conditions: [],
    actions: [],
    status: 'active'
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a rule name');
      return;
    }

    if (formData.actions.length === 0) {
      toast.error('Please add at least one action');
      return;
    }

    try {
      setCreating(true);

      await axios.post('/api/automation/rules', formData);

      toast.success('Rule created successfully');
      onSuccess();
    } catch (error) {
      console.error('Error creating rule:', error);
      toast.error(error.response?.data?.error || 'Failed to create rule');
    } finally {
      setCreating(false);
    }
  };

  const addCondition = () => {
    setFormData({
      ...formData,
      conditions: [
        ...formData.conditions,
        { type: 'sentiment', operator: 'equals', value: 'positive' }
      ]
    });
  };

  const addAction = () => {
    setFormData({
      ...formData,
      actions: [
        ...formData.actions,
        { type: 'tag_buyer', config: { tag: 'Hot Lead' } }
      ]
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 my-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">
          Create Automation Rule
        </h3>

        <div className="space-y-6">
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rule Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Hot Lead Follow-up"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this rule does..."
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Trigger */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trigger
            </label>
            <select
              value={formData.trigger_type}
              onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="response_received">When buyer responds</option>
              <option value="link_clicked">When buyer clicks link</option>
              <option value="time_delay">After time delay</option>
              <option value="manual">Manual trigger</option>
            </select>
          </div>

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Conditions (optional)
              </label>
              <button
                onClick={addCondition}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                + Add Condition
              </button>
            </div>

            {formData.conditions.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                No conditions - rule will always execute when triggered
              </p>
            ) : (
              <div className="space-y-2">
                {formData.conditions.map((condition, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg flex items-center gap-2">
                    <select className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded">
                      <option value="sentiment">Sentiment</option>
                      <option value="keyword">Keyword</option>
                      <option value="response_count">Response count</option>
                    </select>
                    <select className="px-2 py-1 text-sm border border-gray-300 rounded">
                      <option value="equals">equals</option>
                      <option value="contains">contains</option>
                    </select>
                    <input
                      type="text"
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder="Value"
                    />
                    <button
                      onClick={() => setFormData({
                        ...formData,
                        conditions: formData.conditions.filter((_, i) => i !== index)
                      })}
                      className="text-red-600 hover:text-red-700"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Actions *
              </label>
              <button
                onClick={addAction}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                + Add Action
              </button>
            </div>

            {formData.actions.length === 0 ? (
              <p className="text-sm text-red-600">
                Please add at least one action
              </p>
            ) : (
              <div className="space-y-2">
                {formData.actions.map((action, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg flex items-center gap-2">
                    <select className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded">
                      <option value="tag_buyer">Tag buyer</option>
                      <option value="send_email">Send email</option>
                      <option value="send_sms">Send SMS</option>
                      <option value="update_status">Update status</option>
                      <option value="mark_dnc">Mark as DNC</option>
                    </select>
                    <input
                      type="text"
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder="Configuration..."
                    />
                    <button
                      onClick={() => setFormData({
                        ...formData,
                        actions: formData.actions.filter((_, i) => i !== index)
                      })}
                      className="text-red-600 hover:text-red-700"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            disabled={creating}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={creating}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                Creating...
              </>
            ) : (
              'Create Rule'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomationPage;
